import { makeAutoObservable, runInAction } from 'mobx'
import agent from '../api/agent'
import { PagedResult } from '../models/pagedResult'
import { Photo, Profile, ProfileActivityItemDto } from '../models/profile'
import { getRootStore } from './rootStore'

export default class ProfileStore {
  profile: Profile | null = null
  loadingProfile = false
  activityFeed: PagedResult<ProfileActivityItemDto> | null = null
  loadingFeed = false
  uploading = false
  uploadingCover = false
  loading = false
  updatingFollow = false

  constructor () {
    makeAutoObservable(this)
  }

  get isCurrentUser () {
    if (getRootStore().userStore.user && this.profile) {
      return getRootStore().userStore.user.username === this.profile.username
    }
    return false
  }

  get canEdit () {
    return this.isCurrentUser || getRootStore().userStore.isAdmin
  }

  loadProfile = async (username: string) => {
    this.loadingProfile = true
    try {
      const profile = await agent.Profiles.get(username)
      runInAction(() => {
        this.profile = profile
        this.loadingProfile = false
      })
    } catch (error) {
      console.log(error)
      runInAction(() => this.loadingProfile = false)
    }
  }

  updateProfile = async (
    displayName: string,
    bio?: string,
    coverImageUrl?: string | null,
    isVaultPublic?: boolean,
    isWishlistPublic?: boolean
  ) => {
    if (!this.profile) return
    try {
      const updated = await agent.Profiles.update(this.profile.username, {
        displayName,
        bio: bio ?? '',
        coverImageUrl: coverImageUrl ?? undefined,
        isVaultPublic,
        isWishlistPublic
      })
      runInAction(() => {
        this.profile = updated
        if (getRootStore().userStore.user?.username === updated.username) {
          getRootStore().userStore.setDisplayName(updated.displayName)
        }
      })
    } catch (error) {
      throw error
    }
  }

  uploadPhoto = async (file: Blob, setAsMain = false) => {
    this.uploading = true
    try {
      const response = await agent.Profiles.uploadPhoto(file)
      const photo = response.data
      runInAction(() => {
        if (this.profile) {
          this.profile.photos = this.profile.photos ?? []
          this.profile.photos.push(photo)
          if (photo.isMain && getRootStore().userStore.user) {
            getRootStore().userStore.setImage(photo.url)
            this.profile.image = photo.url
          }
        }
        this.uploading = false
      })
      if (setAsMain && !photo.isMain) {
        await this.setMainPhoto(photo)
      }
      return photo
    } catch (error) {
      runInAction(() => (this.uploading = false))
      throw error
    }
  }

  setMainPhoto = async (photo:Photo) => {
    this.loading = true
    try {
      await agent.Profiles.setMainPhoto(photo.id)
      getRootStore().userStore.setImage(photo.url)
      runInAction(() => {
        if (this.profile && this.profile.photos) {
                    this.profile.photos.find(p => p.isMain)!.isMain = false
                    this.profile.photos.find(p => p.id === photo.id)!.isMain = true
                    this.profile.image = photo.url
                    this.loading = false
        }
      })
    } catch (error) {
      runInAction(() => this.loading = false)
      console.log(error)
    }
  }

  deletePhoto = async (photo:Photo) => {
    this.loading = true
    try {
      await agent.Profiles.deletePhoto(photo.id)
      runInAction(() => {
        if (this.profile) {
          this.profile.photos = this.profile.photos?.filter(p => p.id !== photo.id)
          this.loading = false
        }
      })
    } catch (error) {
      runInAction(() => this.loading = false)
      console.log(error)
    }
  }

  uploadCover = async (username: string, file: File) => {
    this.uploadingCover = true
    try {
      const updated = await agent.Profiles.uploadCover(username, file)
      runInAction(() => {
        this.profile = updated
        this.uploadingCover = false
      })
    } catch (error) {
      runInAction(() => (this.uploadingCover = false))
      throw error
    }
  }

  follow = async (username: string) => {
    if (!this.profile) return
    this.updatingFollow = true
    const prevFollowing = this.profile.isFollowing
    const prevCount = this.profile.followersCount ?? 0
    runInAction(() => {
            this.profile!.isFollowing = true
            this.profile!.followersCount = (this.profile!.followersCount ?? 0) + 1
    })
    try {
      await agent.Profiles.follow(username)
    } catch (error) {
      runInAction(() => {
                this.profile!.isFollowing = prevFollowing
                this.profile!.followersCount = prevCount
      })
      throw error
    } finally {
      runInAction(() => (this.updatingFollow = false))
    }
  }

  unfollow = async (username: string) => {
    if (!this.profile) return
    this.updatingFollow = true
    const prevFollowing = this.profile.isFollowing
    const prevCount = this.profile.followersCount ?? 0
    runInAction(() => {
            this.profile!.isFollowing = false
            this.profile!.followersCount = Math.max(0, (this.profile!.followersCount ?? 0) - 1)
    })
    try {
      await agent.Profiles.unfollow(username)
    } catch (error) {
      runInAction(() => {
                this.profile!.isFollowing = prevFollowing
                this.profile!.followersCount = prevCount
      })
      throw error
    } finally {
      runInAction(() => (this.updatingFollow = false))
    }
  }

  loadFollowers = async (username: string) => {
    return agent.Profiles.listFollowers(username)
  }

  loadFollowing = async (username: string) => {
    return agent.Profiles.listFollowing(username)
  }

  loadActivityFeed = async (username: string, pageNumber = 1, pageSize = 20) => {
    this.loadingFeed = true
    this.activityFeed = null
    try {
      const result = await agent.Profiles.getActivityFeed(username, pageNumber, pageSize)
      runInAction(() => {
        this.activityFeed = result
        this.loadingFeed = false
      })
    } catch (error) {
      runInAction(() => (this.loadingFeed = false))
      throw error
    }
  }
}
